<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <h3>{{ $route.meta.title }}</h3>
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>用户管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <layout-panel>
      <div class="config-container">
        <div class="config-form">
          <el-form ref="configForm"
                   :model="form"
                   :rules="newUserConfigRules"
                   size="small">
            <el-form-item label="符合以下"
                          prop="condition_type">
              <el-select v-model="form.condition_type"
                         placeholder="请选择条件类型"
                         :disabled="isViewPage"
                         style="width: 120px;">
                <el-option label="所有"
                           value="all"></el-option>
                <el-option label="任一"
                           value="any"></el-option>
              </el-select>
              <span class="condition-text">条件</span>
            </el-form-item>

            <div class="condition-list">
              <div v-for="(condition, index) in form.conditions"
                   :key="condition.id"
                   class="condition-item">
                <span class="condition-number">{{ index + 1 }}.</span>
                <el-form-item :prop="`conditions.${index}.type`"
                              :rules="{required: true, message: '请选择条件'}"
                              class="condition-form-item">
                  <el-select v-model="condition.type"
                             placeholder="请选择条件"
                             :disabled="isViewPage"
                             class="condition-select"
                             @change="handleConditionTypeChange(condition, index)">
                    <el-option v-for="opt in conditionOptionsMap[index]"
                               :key="opt.value"
                               :label="opt.label"
                               :value="opt.value"
                               :disabled="opt.disabled">
                    </el-option>
                  </el-select>
                </el-form-item>

                <el-form-item :prop="`conditions.${index}.operator`"
                              :rules="{required: true, message: '请选择运算符'}"
                              class="condition-form-item">
                  <el-select v-model="condition.operator"
                             :disabled="isViewPage"
                             class="operator-select">
                    <el-option label="小于"
                               value="lt"></el-option>
                    <el-option label="等于"
                               value="eq"></el-option>
                    <el-option label="大于"
                               value="gt"></el-option>
                    <el-option label="小于等于"
                               value="lte"></el-option>
                    <el-option label="大于等于"
                               value="gte"></el-option>
                  </el-select>
                </el-form-item>

                <el-form-item :prop="`conditions.${index}.value`"
                              :rules="valueRulesMap[condition.type]"
                              class="condition-form-item">
                  <el-input v-model="condition.value"
                            :disabled="isViewPage"
                            placeholder="请输入数值"
                            class="value-input"></el-input>
                </el-form-item>

                <span class="unit-label">{{ getUnit(condition.type) }}</span>

                <el-button type="text"
                           icon="el-icon-delete"
                           class="delete-btn"
                           @click="removeCondition(index)"
                           v-if="form.conditions.length > 1 && !isViewPage">删除
                </el-button>
              </div>
            </div>

            <el-form-item>
              <el-button type="primary"
                         icon="el-icon-plus"
                         size="small"
                         @click="addCondition"
                         :disabled="form.conditions.length >= 3 || isViewPage"
                         plain>添加条件
              </el-button>
              <el-button type="success"
                         size="small"
                         v-if="!isViewPage"
                         :loading="isSaving"
                         @click="onSubmit">保存配置
              </el-button>
            </el-form-item>

            <div class="config-notes">
              <el-alert title="配置说明"
                        type="info"
                        :closable="false"
                        show-icon>
                <div class="notes-content">
                  <p>• 用户需要满足此处配置的规则才会被判定为新手</p>
                  <p>• 每个条件类型只能选择一次</p>
                  <p>• 最多可添加3个条件</p>
                  <p style="color:red;">* 注：所有带了“排他标签”的用户，都不算新用户</p>
                </div>
              </el-alert>
            </div>
          </el-form>
        </div>
      </div>
    </layout-panel>
  </div>
</template>

<script>
import { newUserConfigRules } from '../users.rules'

export default {
  data () {
    return {
      isSaving: false,
      newUserConfigRules,
      conditionIdCounter: 1,
      form: {
        condition_type: 'all',
        conditions: [
          {
            id: 0,
            type: '',
            operator: 'lte',
            value: ''
          }
        ]
      },
      conditionOptions: [
        {value: 'register_days', label: '注册时长', unit: '天'},
        {value: 'finished_task_count', label: '完成任务数', unit: '个'},
        {value: 'total_reward', label: '累计获得赏金', unit: '元'}
      ]
    }
  },
  created () {
    // 预构建校验规则缓存，避免每次渲染创建新对象
    var integerValidator = function (rule, value, callback) {
      if (value === '' || value === null || value === undefined) {
        callback(new Error('请输入数值'))
        return
      }
      var num = Number(value)
      if (isNaN(num)) {
        callback(new Error('请输入有效的数字'))
        return
      }
      if (!Number.isInteger(num)) {
        callback(new Error('请输入整数'))
        return
      }
      if (num <= 0) {
        callback(new Error('数值必须是正数'))
        return
      }
      callback()
    }
    var amountValidator = function (rule, value, callback) {
      if (value === '' || value === null || value === undefined) {
        callback(new Error('请输入数值'))
        return
      }
      // 校验金额格式：整数或最多两位小数，拒绝 "."、".5"、"10." 等不规范格式
      if (!/^\d+(\.\d{1,2})?$/.test(value)) {
        callback(new Error('请输入有效的金额，最多两位小数'))
        return
      }
      var num = Number(value)
      if (num <= 0) {
        callback(new Error('金额必须大于0'))
        return
      }
      callback()
    }
    var integerRules = [
      {required: true, message: '请输入数值'},
      {validator: integerValidator, trigger: 'blur'}
    ]
    var amountRules = [
      {required: true, message: '请输入数值'},
      {validator: amountValidator, trigger: 'blur'}
    ]
    var defaultRules = [{required: true, message: '请输入数值'}]
    this._valueRulesCache = {
      register_days: integerRules,
      finished_task_count: integerRules,
      total_reward: amountRules,
      '': defaultRules
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => vm.onLoad())
  },
  computed: {
    isViewPage: function () {
      return this.$route.meta.pageType === 'view'
    },
    // 为每个条件行计算可用的选项（已选条件置灰）
    conditionOptionsMap: function () {
      var conditions = this.form.conditions
      var options = this.conditionOptions
      return conditions.map(function (condition) {
        return options.map(function (option) {
          return {
            value: option.value,
            label: option.label,
            unit: option.unit,
            disabled: conditions.some(function (c) {
              return c.type === option.value && c.type !== condition.type
            })
          }
        })
      })
    },
    // 根据条件类型映射校验规则
    valueRulesMap: function () {
      return this._valueRulesCache || {}
    }
  },
  methods: {
    onLoad () {
      this.getConfig()
    },
    getConfig () {
      const initLoading = this.$loading({ text: '配置拉取中...', lock: true })
      request.common('getNewbieDefineCondApi', {}).then(({data}) => {
        const config = _.get(data, 'value.config', null)
        if (config) {
          this.form.condition_type = config.condition_type || 'all'

          const self = this
          const defaultCondition = {id: self.conditionIdCounter++, type: '', operator: 'lte', value: ''}
          const loadedConditions = Array.isArray(config.conditions) && config.conditions.length > 0
            ? config.conditions.map(function (c) {
              return {
                id: self.conditionIdCounter++,
                type: c.type || '',
                operator: c.operator || 'lte',
                value: c.value !== undefined && c.value !== null ? String(c.value) : ''
              }
            })
            : [defaultCondition]

          this.$set(this.form, 'conditions', loadedConditions)
        }
      }).catch(() => {
        this.$message.error('配置拉取失败，请刷新页面')
      }).finally(() => {
        initLoading.close()
      })
    },
    onSubmit () {
      if (!filter.hasPermission(20001)) {
        this.$alert('您没有保存 新用户定义配置 的权限')
        return
      }

      this.$refs.configForm.validate(valid => {
        if (valid) {
          if (this.form.conditions.some(c => !c.type)) {
            this.$message.error('请为所有条件选择条件类型')
            return
          }
          this.saveConfig()
        } else {
          this.$message.error('表单填写不正确，请检查')
        }
      })
    },
    saveConfig () {
      const self = this
      const params = {
        config: {
          condition_type: this.form.condition_type,
          conditions: this.form.conditions.map(function (condition) {
            return {
              type: condition.type,
              operator: condition.operator,
              value: condition.value,
              unit: self.getUnit(condition.type)
            }
          })
        }
      }
      this.isSaving = true
      request.common('setNewbieDefineCondApi', params).then(() => {
        this.$alert('配置保存成功')
      }).catch(() => {
        this.$message.error('配置保存失败')
      }).finally(() => {
        this.isSaving = false
      })
    },
    addCondition () {
      if (this.form.conditions.length >= 3) {
        this.$message.warning('最多只能添加3个条件')
        return
      }
      this.form.conditions.push({
        id: this.conditionIdCounter++,
        type: '',
        operator: 'lte',
        value: ''
      })
    },
    removeCondition (index) {
      if (this.form.conditions.length <= 1) {
        this.$message.warning('至少需要保留一个条件')
        return
      }
      this.form.conditions.splice(index, 1)
      this.$nextTick(() => {
        this.$refs.configForm.clearValidate()
      })
    },
    handleConditionTypeChange (condition, index) {
      condition.value = ''
      this.$nextTick(() => {
        if (this.$refs.configForm) {
          this.$refs.configForm.clearValidate(`conditions.${index}.value`)
        }
      })
    },
    getUnit (type) {
      var option = this.conditionOptions.find(function (opt) { return opt.value === type })
      return option ? option.unit : ''
    }
  }
}
</script>

<style lang="scss"
       scoped>
.config-container {
  padding: 10px 20px;

  .config-form {
    max-width: 800px;
  }

  .config-notes {
    margin-top: 15px;

    .notes-content {
      font-size: 13px;
      line-height: 1.6;
      color: #606266;

      p {
        margin: 0;
      }
    }
  }
}

.condition-list {
  margin: 10px 0;
}

.condition-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px 10px;
  background-color: #f5f7fa;
  border-radius: 4px;

  .condition-number {
    font-size: 14px;
    color: #606266;
    font-weight: 500;
    width: 18px;
  }

  .condition-form-item {
    margin-bottom: 0;
  }

  .condition-select {
    width: 100%;
  }

  .operator-select {
    width: 100%;
  }

  .value-input {
    width: 100%;
  }

  .unit-label {
    font-size: 14px;
    color: #606266;
    min-width: 20px;
    margin-left: 2px;
  }

  .delete-btn {
    padding: 0;
    margin-left: 5px;
  }
}

.condition-text {
  margin-left: 8px;
  color: #606266;
}
</style>



// WEBPACK FOOTER //
// src/views/users/pages/UsersNewbieCfg.vue